import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { MiniPlayer, MiniPlayerHandle } from './MiniPlayer';
import { FullPlayer, FullLayer, FullPlayerHandle } from './FullPlayer';
import {
  ChatComment,
  LiveProduct,
  LiveSessionPublic,
  centsToCurrency,
  fetchShopifyCart,
  getAnonId,
  mapCartLines,
  sameProductId,
} from './utils';
import {
  ensureCatalog,
  getCatalogProduct,
  getVariantById,
} from './catalog';
import {
  ensureMetaClickIds,
  getMetaClickIds,
  META_FBC_ATTR,
  META_FBP_ATTR,
  syncMetaClickIdsToCart,
} from './metaClickIds';
import { clearQuoteCache } from './pricing';

const LIVE_CURRENCY = 'MYR';

/** Meta CAPI custom_data.value / currency for commerce events. */
function moneyMeta(
  value: number | null | undefined,
  currency: string = LIVE_CURRENCY
): { value: number; currency: string } | Record<string, never> {
  if (value == null || Number.isNaN(Number(value))) return {};
  return { value: Number(Number(value).toFixed(2)), currency: currency.toUpperCase() };
}

async function catalogUnitPrice(
  productId: string,
  variantId?: string
): Promise<number | null> {
  await ensureCatalog();
  const info = getCatalogProduct(productId);
  if (!info) return null;
  const variant = getVariantById(info, variantId || info.variantId);
  const raw = variant?.price ?? info.price;
  if (raw == null) return null;
  const n = Number(raw);
  return Number.isNaN(n) ? null : n;
}

interface Props {
  apiUrl: string;
  sessionId?: string;
  /** Start in full player (from ?fullscreen=true or init config). */
  initialFullscreen?: boolean;
}

function applyShowcase(
  prev: LiveSessionPublic,
  productId: string | null
): LiveSessionPublic {
  return {
    ...prev,
    showcased_product_id: productId,
    products: (prev.products || []).map((p) => ({
      ...p,
      pinned: productId != null && sameProductId(p.item_id, productId),
    })),
  };
}

export function LiveApp({
  apiUrl,
  sessionId,
  initialFullscreen = false,
}: Props) {
  const [session, setSession] = useState<LiveSessionPublic | null>(null);
  // Always start mini — ?fullscreen only opens after a live session is confirmed,
  // so ended/missing lives never leave body scroll locked with no player.
  const [mode, setMode] = useState<'mini' | 'full'>('mini');
  const [layer, setLayer] = useState<FullLayer>('live');
  /** Shared across mini ↔ full. Full enter and mini-dock both force muted. */
  const [muted, setMuted] = useState(true);
  const [selectedProduct, setSelectedProduct] = useState<LiveProduct | null>(null);
  const [comments, setComments] = useState<ChatComment[]>([]);
  const [cartCount, setCartCount] = useState(0);
  const [socialAtc, setSocialAtc] = useState(0);
  const [productViews, setProductViews] = useState<Record<string, number>>({});
  const [viewerCount, setViewerCount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const eventQueue = useRef<any[]>([]);
  const viewTimers = useRef<{ t10?: number; t60?: number; t5m?: number }>({});
  const fullPlayerRef = useRef<FullPlayerHandle>(null);
  const miniPlayerRef = useRef<MiniPlayerHandle>(null);
  const bootedFullscreen = useRef(false);
  const anonId = useMemo(() => getAnonId(), []);

  const resolvedSessionId = session?.id || sessionId;
  const isLive = session?.status === 'live';

  /** Lightweight badge count only — never run discount cart sync here (that hits /cart.js repeatedly). */
  const refreshCartCount = useCallback(async () => {
    try {
      const cart = await fetchShopifyCart();
      const lines = mapCartLines(cart);
      setCartCount(lines.reduce((sum, l) => sum + l.quantity, 0));
    } catch {
      // ignore
    }
  }, []);

  const flushEvents = useCallback(async () => {
    if (!resolvedSessionId || eventQueue.current.length === 0) return;
    const batch = eventQueue.current.splice(0, eventQueue.current.length);
    const { fbc, fbp } = getMetaClickIds();
    try {
      await fetch(`${apiUrl}/live/${resolvedSessionId}/events`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          events: batch,
          event_source_url: typeof window !== 'undefined' ? window.location.href : undefined,
          ...(fbc ? { fbc } : {}),
          ...(fbp ? { fbp } : {}),
        }),
      });
    } catch {
      eventQueue.current.unshift(...batch);
    }
  }, [apiUrl, resolvedSessionId]);

  const queueEvent = useCallback(
    (event: {
      event_type: string;
      product_id?: string;
      meta?: Record<string, unknown>;
    }) => {
      // Server records + Meta CAPI only (no browser Pixel) to avoid double-counting
      const { fbc, fbp } = getMetaClickIds();
      eventQueue.current.push({
        ...event,
        anon_id: anonId,
        meta: {
          ...(event.meta || {}),
          ...(fbc ? { fbc } : {}),
          ...(fbp ? { fbp } : {}),
          event_source_url:
            typeof window !== 'undefined' ? window.location.href : undefined,
        },
      });
      if (eventQueue.current.length >= 5) flushEvents();
    },
    [anonId, flushEvents]
  );

  const trackEngage = useCallback(
    (reason: 'comment' | 'bag' | 'product') => {
      queueEvent({ event_type: 'engage', meta: { engage_type: reason } });
    },
    [queueEvent]
  );

  const loadSession = useCallback(async () => {
    try {
      setError(null);
      const url = sessionId
        ? `${apiUrl}/live/${sessionId}/public`
        : `${apiUrl}/live/current`;
      const res = await fetch(url);
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || 'Failed to load live');
      if (!data.data) {
        setSession(null);
        return;
      }
      setSession(data.data);
      setViewerCount(
        data.data.social_proof?.viewer_count ?? data.data.viewer_count ?? 0
      );
      setSocialAtc(data.data.social_proof?.add_to_cart_last_5_min || 0);
      setProductViews(data.data.social_proof?.product_views || {});
    } catch (err: any) {
      setError(err.message || 'Failed to load live');
    }
  }, [apiUrl, sessionId]);

  useEffect(() => {
    ensureMetaClickIds();
    void syncMetaClickIdsToCart();
  }, []);

  useEffect(() => {
    loadSession();
    refreshCartCount();
  }, [loadSession, refreshCartCount]);

  // ?fullscreen=true — open full player only once a live session is confirmed
  useEffect(() => {
    if (!initialFullscreen || !session || session.status !== 'live' || bootedFullscreen.current) {
      return;
    }
    bootedFullscreen.current = true;
    flushSync(() => {
      setMode('full');
      setLayer('live');
    });
    requestAnimationFrame(() => {
      fullPlayerRef.current?.startPlayback();
    });
  }, [initialFullscreen, session]);

  // Live ended / not live — leave full player so the storefront is not blocked
  useEffect(() => {
    if (mode !== 'full') return;
    if (session?.status === 'live') return;
    fullPlayerRef.current?.stopPlayback();
    setMode('mini');
    setLayer('live');
    setSelectedProduct(null);
  }, [mode, session?.status]);

  // Product removed via SSE while its sheet is open
  useEffect(() => {
    if (layer === 'product' && !selectedProduct) setLayer('bag');
  }, [layer, selectedProduct]);

  useEffect(() => {
    // Wait until session is loaded so the initial SSE showcase event is not dropped
    if (!session?.id) return;
    const es = new EventSource(`${apiUrl}/live/${session.id}/stream`);
    es.onmessage = (msg) => {
      try {
        const event = JSON.parse(msg.data);
        if (event.type === 'session_status') {
          setSession((prev) => (prev ? { ...prev, status: event.status } : prev));
        }
        if (event.type === 'showcase') {
          const productId =
            event.product_id == null || event.product_id === ''
              ? null
              : String(event.product_id);
          setSession((prev) => (prev ? applyShowcase(prev, productId) : prev));
        }
        if (event.type === 'products' && Array.isArray(event.products)) {
          const showcased =
            event.showcased_product_id == null || event.showcased_product_id === ''
              ? null
              : String(event.showcased_product_id);
          setSession((prev) =>
            prev
              ? {
                  ...prev,
                  products: event.products,
                  showcased_product_id: showcased,
                }
              : prev
          );
          setSelectedProduct((prev) => {
            if (!prev) return prev;
            const stillThere = event.products.some((p: { item_id?: string }) =>
              sameProductId(p.item_id, prev.item_id)
            );
            return stillThere ? prev : null;
          });
        }
        if (event.type === 'viewer_count') {
          setViewerCount(event.count ?? event.viewer_count ?? 0);
        }
        if (event.type === 'social_proof') {
          setSocialAtc(event.add_to_cart_last_5_min || 0);
          if (event.product_views) setProductViews(event.product_views);
          if (event.viewer_count != null) setViewerCount(event.viewer_count);
        }
        if (event.type === 'chat') setComments(event.comments || []);
        if (event.type === 'discounts' && Array.isArray(event.discounts)) {
          clearQuoteCache();
          setSession((prev) => (prev ? { ...prev, discounts: event.discounts } : prev));
        }
      } catch {
        // ignore
      }
    };
    es.onerror = () => {
      // browser will retry; keep stream alive
    };
    return () => es.close();
  }, [apiUrl, session?.id]);

  // Concurrent viewer count — heartbeat only while fullscreen (mini does not count)
  useEffect(() => {
    if (!resolvedSessionId || mode !== 'full') return;
    const tick = async () => {
      try {
        const res = await fetch(`${apiUrl}/live/${resolvedSessionId}/heartbeat`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ anon_id: anonId }),
        });
        const data = await res.json();
        if (data?.data?.viewer_count != null) setViewerCount(data.data.viewer_count);
        if (data?.data?.social_proof) {
          setSocialAtc(data.data.social_proof.add_to_cart_last_5_min || 0);
          if (data.data.social_proof.product_views) {
            setProductViews(data.data.social_proof.product_views);
          }
          if (data.data.social_proof.viewer_count != null) {
            setViewerCount(data.data.social_proof.viewer_count);
          }
        }
      } catch {
        // ignore
      }
    };
    tick();
    const timer = setInterval(tick, 15000);
    return () => clearInterval(timer);
  }, [apiUrl, resolvedSessionId, anonId, mode]);

  // View / LiveView* timers only while the full-screen player is open
  useEffect(() => {
    if (!resolvedSessionId || mode !== 'full') return;
    queueEvent({ event_type: 'view_start' });
    viewTimers.current.t10 = window.setTimeout(
      () => queueEvent({ event_type: 'view_10s' }),
      10000
    );
    viewTimers.current.t60 = window.setTimeout(
      () => queueEvent({ event_type: 'view_60s' }),
      60000
    );
    viewTimers.current.t5m = window.setTimeout(
      () => queueEvent({ event_type: 'view_5m' }),
      300000
    );
    const flushTimer = setInterval(flushEvents, 4000);

    fetch(`${apiUrl}/live/${resolvedSessionId}/chat`)
      .then((r) => r.json())
      .then((d) => {
        if (d?.success && d.data?.comments) setComments(d.data.comments);
      })
      .catch(() => undefined);

    return () => {
      if (viewTimers.current.t10) clearTimeout(viewTimers.current.t10);
      if (viewTimers.current.t60) clearTimeout(viewTimers.current.t60);
      if (viewTimers.current.t5m) clearTimeout(viewTimers.current.t5m);
      viewTimers.current = {};
      queueEvent({ event_type: 'view_end' });
      flushEvents();
      clearInterval(flushTimer);
    };
  }, [resolvedSessionId, mode, queueEvent, flushEvents, apiUrl]);

  useEffect(() => {
    // Only lock scroll while the full player is actually open over a live session
    if (mode === 'full' && isLive) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [mode, isLive]);

  const openFull = useCallback(() => {
    flushSync(() => {
      setMode('full');
      setLayer('live');
    });
    fullPlayerRef.current?.startPlayback();
  }, []);

  const closeFull = useCallback(() => {
    fullPlayerRef.current?.stopPlayback();
    // Remount mini inside the same close gesture so unmuted autoplay is allowed on iOS.
    // Always reset layer so the LIVE cart modal unmounts with the player (never leaks to page).
    flushSync(() => {
      setMode('mini');
      setLayer('live');
      setSelectedProduct(null);
    });
    miniPlayerRef.current?.startPlayback();
  }, []);

  /** Line properties every live-originated cart line carries (attribution + Meta match). */
  const liveLineProperties = useCallback((): Record<string, string> => {
    const properties: Record<string, string> = {};
    if (resolvedSessionId) {
      // Underscore = hidden on storefront; used for live sales attribution on order webhook
      properties._intan_live_session_id = resolvedSessionId;
    }
    const { fbc, fbp } = getMetaClickIds();
    if (fbc) properties[META_FBC_ATTR] = fbc;
    if (fbp) properties[META_FBP_ATTR] = fbp;
    return properties;
  }, [resolvedSessionId]);

  const addToCart = async (
    productId: string,
    variantId?: string
  ): Promise<{ value: number | null; currency: string }> => {
    let resolvedVariantId = variantId;
    if (!resolvedVariantId) {
      await ensureCatalog();
      resolvedVariantId = getCatalogProduct(productId)?.variantId || undefined;
    }
    if (!resolvedVariantId) throw new Error('Product unavailable');
    const properties = liveLineProperties();
    const addRes = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: Number(resolvedVariantId) || resolvedVariantId,
        quantity: 1,
        ...(Object.keys(properties).length > 0 ? { properties } : {}),
      }),
    });
    const added = await addRes.json().catch(() => null);
    // Shopify /cart/add.js returns line price in cents
    let value =
      added?.price != null && !Number.isNaN(Number(added.price))
        ? centsToCurrency(Number(added.price))
        : null;
    if (value == null) {
      value = await catalogUnitPrice(productId, resolvedVariantId);
    }
    const currency =
      typeof added?.currency === 'string' && added.currency
        ? String(added.currency).toUpperCase()
        : LIVE_CURRENCY;
    void syncMetaClickIdsToCart();
    queueEvent({
      event_type: 'add_to_cart',
      product_id: productId,
      meta: {
        ...moneyMeta(value, currency),
        variant_id: resolvedVariantId,
      },
    });
    flushEvents();
    await refreshCartCount();
    return { value, currency };
  };

  /** Buy now → clear cart, add just this item, open cart modal over LIVE */
  const buyNow = async (productId: string, variantId?: string) => {
    await fetch('/cart/clear.js', { method: 'POST', credentials: 'same-origin' });
    const { value, currency } = await addToCart(productId, variantId);
    queueEvent({
      event_type: 'checkout_start',
      product_id: productId,
      meta: moneyMeta(value, currency),
    });
    flushEvents();
    setSelectedProduct(null);
    setLayer('cart');
  };

  /**
   * Combo (BOGO placeholder) add-to-cart: the placeholder itself is never added —
   * only the real variants the shopper picked in the combo wizard. The cart
   * discount engine then re-matches the BOGO rule and prices the group down.
   */
  const addComboToCart = async (
    productId: string,
    lines: Array<{ variantId: string; quantity: number }>
  ): Promise<{ value: number | null; currency: string }> => {
    if (!lines.length) throw new Error('Nothing selected');
    const properties = liveLineProperties();
    const hasProps = Object.keys(properties).length > 0;
    const res = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        items: lines.map((line) => ({
          id: Number(line.variantId) || line.variantId,
          quantity: line.quantity,
          ...(hasProps ? { properties } : {}),
        })),
      }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => null);
      throw new Error(err?.description || err?.message || 'Failed to add combo to cart');
    }
    const added = await res.json().catch(() => null);
    // Multi-line /cart/add.js returns { items: [...] } with line_price in cents
    const addedItems: any[] = Array.isArray(added?.items) ? added.items : [];
    const cents = addedItems.reduce(
      (sum, item) => sum + (Number(item?.line_price ?? item?.price) || 0),
      0
    );
    const value = cents > 0 ? centsToCurrency(cents) : null;
    const currency =
      typeof added?.currency === 'string' && added.currency
        ? String(added.currency).toUpperCase()
        : LIVE_CURRENCY;

    void syncMetaClickIdsToCart();
    queueEvent({
      event_type: 'add_to_cart',
      product_id: productId,
      meta: {
        ...moneyMeta(value, currency),
        combo: true,
        line_count: lines.length,
        variant_ids: lines.map((l) => l.variantId).join(','),
      },
    });
    flushEvents();
    await refreshCartCount();
    return { value, currency };
  };

  const buyNowCombo = async (
    productId: string,
    lines: Array<{ variantId: string; quantity: number }>
  ) => {
    await fetch('/cart/clear.js', { method: 'POST', credentials: 'same-origin' });
    const { value, currency } = await addComboToCart(productId, lines);
    queueEvent({
      event_type: 'checkout_start',
      product_id: productId,
      meta: { ...moneyMeta(value, currency), combo: true },
    });
    flushEvents();
    setSelectedProduct(null);
    setLayer('cart');
  };

  const sendChat = async (message: string, displayName: string) => {
    if (!resolvedSessionId) return;
    const res = await fetch(`${apiUrl}/live/${resolvedSessionId}/chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        anon_id: anonId,
        display_name: displayName,
        message,
      }),
    });
    const data = await res.json();
    if (!res.ok || !data.success) {
      throw new Error(data.message || 'Failed to send');
    }
    trackEngage('comment');
    flushEvents();
  };

  if (error && !session) {
    return null;
  }

  // No session, or session is not live — render nothing and do not block the page
  if (!session || !isLive) {
    return null;
  }

  return (
    <div className="intan-live-root">
      {mode === 'mini' && (
        <MiniPlayer
          ref={miniPlayerRef}
          session={session}
          muted={muted}
          onMutedChange={setMuted}
          viewerCount={viewerCount}
          onOpen={openFull}
        />
      )}
      <FullPlayer
        ref={fullPlayerRef}
        apiUrl={apiUrl}
        session={session}
        active={mode === 'full'}
        muted={muted}
        onMutedChange={setMuted}
        cartCount={cartCount}
        viewerCount={viewerCount}
        comments={comments}
        layer={layer}
        selectedProduct={selectedProduct}
        productViews={productViews}
        socialAtc={socialAtc}
        onClose={closeFull}
        onOpenCart={() => {
          setSelectedProduct(null);
          setLayer('cart');
          void (async () => {
            try {
              const cart = await fetchShopifyCart();
              const value = centsToCurrency(Number(cart.total_price) || 0);
              const currency =
                typeof cart.currency === 'string' && cart.currency
                  ? String(cart.currency).toUpperCase()
                  : LIVE_CURRENCY;
              queueEvent({
                event_type: 'checkout_start',
                meta: moneyMeta(value > 0 ? value : null, currency),
              });
            } catch {
              queueEvent({ event_type: 'checkout_start' });
            }
            flushEvents();
          })();
        }}
        onOpenProducts={() => {
          setSelectedProduct(null);
          setLayer('bag');
          trackEngage('bag');
          flushEvents();
        }}
        onSelectProduct={(p) => {
          setSelectedProduct(p);
          setLayer('product');
          void (async () => {
            const value = await catalogUnitPrice(p.item_id);
            queueEvent({
              event_type: 'product_view',
              product_id: p.item_id,
              meta: moneyMeta(value),
            });
            trackEngage('product');
            flushEvents();
          })();
        }}
        onBackFromProduct={() => {
          setSelectedProduct(null);
          setLayer('bag');
        }}
        onCloseLayer={() => {
          setLayer('live');
          setSelectedProduct(null);
          refreshCartCount();
        }}
        onAddToCart={async (productId, variantId) => {
          await addToCart(productId, variantId);
        }}
        onBuyNow={buyNow}
        onAddCombo={async (productId, lines) => {
          await addComboToCart(productId, lines);
        }}
        onBuyCombo={buyNowCombo}
        onSendChat={sendChat}
      />
    </div>
  );
}
